import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { customerbasedshift } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';
import Swal from 'sweetalert2';

// ============================================================
// MES Planning — manager cockpit
//   Tab 1: 📊 Today        — current shift status grid
//   Tab 2: 📅 Schedule     — 7-day Gantt of production orders × machines
// ============================================================

const STATUS_COLOR = {
  Running:      { bg: '#dcfce7', border: '#16a34a', text: '#14532d', dot: '#16a34a', icon: '▶' },
  Idle:         { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', dot: '#f59e0b', icon: '‖' },
  Alarm:        { bg: '#fee2e2', border: '#dc2626', text: '#7a0000', dot: '#dc2626', icon: '⚠' },
  Disconnected: { bg: '#f3f4f6', border: '#6b7280', text: '#374151', dot: '#6b7280', icon: '⊘' },
  'No Data':    { bg: '#f9fafb', border: '#9ca3af', text: '#6b7280', dot: '#9ca3af', icon: '?' },
  Unknown:      { bg: '#f9fafb', border: '#9ca3af', text: '#6b7280', dot: '#9ca3af', icon: '?' },
};

const ACTION_COLOR = {
  green: { bg: '#dcfce7', text: '#14532d' },
  amber: { bg: '#fef3c7', text: '#92400e' },
  red:   { bg: '#fee2e2', text: '#7a0000' },
};

const PRIORITY_COLOR = { rush: '#dc2626', high: '#f59e0b', normal: '#3b82f6', low: '#9ca3af' };
const cleanOrderId = (id) => id ? String(id).replace(/::(OP\d+)/i, ' · $1') : '';
const cleanComponent = (comp) => comp ? String(comp).replace(/\s+OP\d+\s*$/i, '').trim() : '';


const fmtTimeHHmm = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const MesPlanning = () => {
  const customerId = localStorage.getItem('CustomerID');
  const cid = cleanCustomerId(customerId);
  const SERVER = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule' first (Gantt) | 'today'

  return (
    <div style={{ padding: '20px 28px 40px', background: '#fafafa', minHeight: 'calc(100vh - 70px)' }}>
      {/* HEADER + TAB BAR */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 22, color: '#111827', fontWeight: 800, letterSpacing: -0.3 }}>
          📊 MES Production Cockpit
        </h2>
        <div style={{ display: 'inline-flex', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: 3 }}>
          {[
            { v: 'today',    label: '📊 Today' },
            { v: 'schedule', label: '📅 Schedule' },
          ].map(t => (
            <button key={t.v} onClick={() => setActiveTab(t.v)} style={{
              padding: '7px 18px', fontSize: 13, fontWeight: 700,
              background: activeTab === t.v ? '#F99022' : 'transparent',
              color: activeTab === t.v ? '#fff' : '#6b7280',
              border: 'none', borderRadius: 999, cursor: 'pointer',
              transition: 'all 150ms ease',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {activeTab === 'today'    && <TodayTab cid={cid} SERVER={SERVER} />}
      {activeTab === 'schedule' && <ScheduleTab cid={cid} customerId={customerId} SERVER={SERVER} />}
    </div>
  );
};

// ============================================================
// TAB 1 — TODAY (existing cockpit)
// ============================================================
const TodayTab = ({ cid, SERVER }) => {
  const [shiftNo, setShiftNo] = useState('1');
  const [shiftEndIso] = useState(() => { const d = new Date(); d.setHours(20, 0, 0, 0); return d.toISOString(); });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchPlan = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${SERVER}/api/buddy/mes/floor-plan`, {
        params: { customer_id: cid, shift_no: shiftNo, shift_end_iso: shiftEndIso },
      });
      setPlan(r.data);
      setLastRefresh(new Date());
    } catch (e) { console.error('MES fetch failed', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPlan();
    const id = setInterval(fetchPlan, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, shiftNo]);

  const totals = plan?.totals || {};
  const machines = plan?.machines || [];
  const overallPct = totals.parts_pct ?? 0;
  const overallColor = overallPct >= 85 ? '#16a34a' : overallPct >= 60 ? '#f59e0b' : '#dc2626';

  const sortedMachines = useMemo(() => {
    const rank = (m) => {
      if (m.status === 'Alarm') return 0;
      if (m.action === 'ASSIGN OPERATOR') return 1;
      if (m.eta_status === 'behind') return 2;
      if (m.status === 'Idle') return 3;
      if (m.eta_status === 'on_track') return 4;
      if (m.eta_status === 'ahead') return 5;
      return 6;
    };
    return [...machines].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [machines]);

  return (
    <>
      {/* Shift selector + refresh */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Shift</span>
        <select value={shiftNo} onChange={(e) => setShiftNo(e.target.value)} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}>
          <option value="1">1</option><option value="2">2</option><option value="3">3</option>
        </select>
        <button onClick={fetchPlan} disabled={loading} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, background: '#F99022', color: '#fff', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer' }}>
          {loading ? '⟳ …' : '⟳ Refresh'}
        </button>
        {lastRefresh && <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</span>}
      </div>

      {/* Floor totals */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 18, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 4 }}>Floor progress</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 32, fontWeight: 900, color: overallColor, lineHeight: 1 }}>{overallPct ?? '—'}%</span>
              <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}><b style={{ color: '#111827' }}>{totals.parts_made ?? 0}</b> / {totals.parts_target ?? 0} parts</span>
            </div>
            <div style={{ height: 10, background: '#f3f4f6', borderRadius: 999 }}>
              <div style={{ height: '100%', width: `${Math.min(100, overallPct || 0)}%`, background: overallColor, borderRadius: 999, transition: 'width 400ms ease' }} />
            </div>
          </div>
          <StatTile label="On track" value={totals.on_track ?? 0} color="#16a34a" icon="✓" />
          <StatTile label="Behind" value={totals.behind ?? 0} color="#dc2626" icon="↓" />
          <StatTile label="Needs op" value={totals.idle_needs_operator ?? 0} color="#f59e0b" icon="👤" />
          <StatTile label="Alarms" value={totals.alarms ?? 0} color="#dc2626" icon="⚠" />
        </div>
      </div>

      {/* Machine grid */}
      {!plan && loading && <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>Loading floor…</div>}
      {plan && machines.length === 0 && <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>No machines for this shift.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
        {sortedMachines.map((m) => <MachineCard key={m.device_id || m.name} m={m} />)}
      </div>
    </>
  );
};

const SummaryPill = ({ icon, label, value, color, bg }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', background: bg, borderRadius: 999, fontSize: 12, fontWeight: 700, color }}>
    <span style={{ fontSize: 11 }}>{icon}</span>
    <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.85, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</span>
  </div>
);

const StatTile = ({ label, value, color, icon }) => (
  <div style={{ textAlign: 'center', padding: '6px 0' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{icon} {value}</div>
  </div>
);

const MachineCard = ({ m }) => {
  const sc = STATUS_COLOR[m.status] || STATUS_COLOR.Unknown;
  const ac = m.action ? (ACTION_COLOR[m.action_color] || ACTION_COLOR.amber) : null;
  const pct = m.parts_pct ?? 0;
  const barColor = pct >= 85 ? '#16a34a' : pct >= 60 ? '#f59e0b' : pct >= 30 ? '#fb923c' : '#dc2626';
  const showAlarm = m.status === 'Alarm';
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: `2px solid ${sc.border}`, boxShadow: showAlarm ? '0 0 0 4px rgba(220,38,38,0.15), 0 2px 6px rgba(0,0,0,0.05)' : '0 1px 3px rgba(0,0,0,0.04)', position: 'relative', animation: showAlarm ? 'mes-pulse 1.6s ease-in-out infinite' : 'none' }}>
      <style>{`@keyframes mes-pulse { 0%,100% { box-shadow: 0 0 0 4px rgba(220,38,38,0.15), 0 2px 6px rgba(0,0,0,0.05); } 50% { box-shadow: 0 0 0 8px rgba(220,38,38,0.06), 0 2px 6px rgba(0,0,0,0.05); } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: '#111827', letterSpacing: -0.2 }}>{m.name}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>
          <span>{sc.icon}</span> {m.status?.toUpperCase()}
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        {m.active_component ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', lineHeight: 1.3 }}>
              {m.active_code && <span style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 3, fontSize: 11, fontFamily: 'monospace', marginRight: 5 }}>{m.active_code}</span>}
              {m.active_component}
            </div>
            {m.program_no && <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace', marginTop: 2 }}>Prog: {m.program_no}</div>}
          </>
        ) : <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No component allocated</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12 }}>
        <span style={{ fontSize: 14 }}>👤</span>
        <span style={{ fontWeight: 700, color: m.operator_name ? '#111827' : '#9ca3af' }}>{m.operator_name || 'unassigned'}</span>
        {m.cycle_time_sec && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6b7280', fontWeight: 600 }}>⏱ {Math.round(m.cycle_time_sec / 60)}m cycle</span>}
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.4, textTransform: 'uppercase' }}>Parts</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
            <b style={{ color: barColor }}>{m.parts_made ?? 0}</b><span style={{ color: '#9ca3af', fontWeight: 500 }}> / {m.parts_target ?? 0}</span>
            <span style={{ marginLeft: 6, fontSize: 11, color: barColor }}>({pct}%)</span>
          </span>
        </div>
        <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999 }}>
          <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: barColor, borderRadius: 999, transition: 'width 400ms ease' }} />
        </div>
      </div>
      {m.eta_ms && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>Target ETA</span>
          <span style={{ fontWeight: 800, color: m.eta_status === 'behind' ? '#dc2626' : m.eta_status === 'ahead' ? '#16a34a' : '#374151' }}>🎯 {fmtTimeHHmm(m.eta_ms)}</span>
        </div>
      )}
      {m.action && ac && (
        <div style={{ padding: '6px 10px', borderRadius: 6, background: ac.bg, color: ac.text, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textAlign: 'center' }}>
          {m.action}
        </div>
      )}
    </div>
  );
};

// ============================================================
// TAB 2 — SCHEDULE (Gantt)
// ============================================================
const ScheduleTab = ({ cid, customerId, SERVER }) => {
  const [orders, setOrders] = useState([]);
  const [components, setComponents] = useState([]);
  const [floorPlan, setFloorPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [horizonDays, setHorizonDays] = useState(7);
  const [includeHolidays, setIncludeHolidays] = useState(false);
  const [saving, setSaving] = useState(false);

  // Time window for Gantt: start of today → +horizonDays
  const t0 = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); }, []);
  const t1 = useMemo(() => t0 + horizonDays * 86400000, [t0, horizonDays]);

  // Decoupled fetches — each result populates its own state independently.
  // Critical: floor-plan can take 25-30s on backend; we DON'T want orders to
  // wait on it. Orders + components arrive fast and render the Gantt; floor-plan
  // streams in when it eventually returns.
  const fetchAll = async () => {
    if (!cid) return;
    setLoading(true);
    let pending = 3;
    const done = () => { if (--pending === 0) setLoading(false); };

    axios.get(`${SERVER}/api/buddy/mes/orders`, { params: { customer_id: cid, include_holidays: includeHolidays } })
      .then((r) => setOrders(Array.isArray(r.data?.orders) ? r.data.orders : []))
      .catch(() => setOrders([]))
      .finally(done);

    customerbasedshift(cid, 'component')
      .then((r) => { const a = r?.[0]?.value || []; setComponents(Array.isArray(a) ? a : []); })
      .catch(() => setComponents([]))
      .finally(done);

    axios.get(`${SERVER}/api/buddy/mes/floor-plan`, { params: { customer_id: cid, shift_no: '1' } })
      .then((r) => setFloorPlan(r.data || null))
      .catch(() => setFloorPlan(null))
      .finally(done);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cid, includeHolidays]);

  // Auto-save: once per load, if the planner produced auto-assignments,
  // silently persist them to TB so other screens (Today, dashboard) see them.
  const autoSavedRef = useRef(false);
  useEffect(() => { autoSavedRef.current = false; }, [cid]);

  // Component cycle time lookup
  const cycleSecByCode = useMemo(() => {
    const m = new Map();
    for (const c of components) {
      const code = String(c.component_number || '').trim();
      const ct = String(c.cycle_time || '').trim();
      if (code && ct) {
        const [h = 0, mn = 0, s = 0] = ct.split(':').map(Number);
        const secs = (h * 3600) + (mn * 60) + s;
        if (secs > 0) m.set(code, secs);
      }
    }
    return m;
  }, [components]);

  // Compute planned_start/end for orders missing them, using simple FIFO+EDD on assigned machines
  const scheduledOrders = useMemo(() => {
    const ASSUMED_START = Date.now();
    const SETUP_MIN = 15 * 60 * 1000; // 15 minute setup between jobs on same machine
    // Order: rush first, then by due date
    const prio = { rush: 0, high: 1, normal: 2, low: 3 };
    const todo = [...orders]
      .filter(o => o.status !== 'done' && o.status !== 'cancelled')
      .sort((a, b) => {
        const pa = prio[a.priority] ?? 2, pb = prio[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
        return new Date(a.due_date || 0) - new Date(b.due_date || 0);
      });

    // Per-machine "next available" time, seed with NOW
    const nextFreeByMachine = new Map();
    const out = [];

    for (const o of todo) {
      const cycleSec = cycleSecByCode.get(o.component_number);
      const qty = Math.max(0, (o.qty_total || 0) - (o.qty_done || 0));
      const durMs = (cycleSec && qty > 0) ? (cycleSec * qty * 1000) : 0;
      // If manually scheduled (assigned_machine + planned_start), respect it
      if (o.assigned_machine && o.planned_start) {
        const start = new Date(o.planned_start).getTime();
        const end = o.planned_end ? new Date(o.planned_end).getTime() : (start + durMs);
        out.push({ ...o, _start: start, _end: end, _auto: false });
        nextFreeByMachine.set(o.assigned_machine, Math.max(end + SETUP_MIN, nextFreeByMachine.get(o.assigned_machine) || 0));
        continue;
      }
      // Auto-plan: pick preferred or any
      const candidates = (o.preferred_machines?.length ? o.preferred_machines : (floorPlan?.machines || []).map(m => m.name));
      let bestMachine = candidates[0] || null;
      let bestFree = nextFreeByMachine.get(bestMachine) || ASSUMED_START;
      for (const m of candidates) {
        const f = nextFreeByMachine.get(m) || ASSUMED_START;
        if (f < bestFree) { bestFree = f; bestMachine = m; }
      }
      const start = bestFree;
      const end = start + durMs;
      out.push({ ...o, _start: start, _end: end, _auto: true, _machine: bestMachine });
      if (bestMachine) nextFreeByMachine.set(bestMachine, end + SETUP_MIN);
    }
    return out;
  }, [orders, cycleSecByCode, floorPlan]);

  // Silent auto-save: when the Schedule tab loads and the planner produced
  // _auto assignments for queued orders, persist them to TB without prompting.
  useEffect(() => {
    if (!cid || loading || saving) return;
    if (autoSavedRef.current) return;
    if (orders.length === 0) return;
    const autos = scheduledOrders.filter(o => o._auto && o._machine);
    if (autos.length === 0) { autoSavedRef.current = true; return; }
    autoSavedRef.current = true;
    (async () => {
      try {
        const updated = orders.map(o => {
          const s = scheduledOrders.find(x => x.order_id === o.order_id);
          if (!s || !s._auto || !s._machine) return o;
          return { ...o, assigned_machine: s._machine, planned_start: new Date(s._start).toISOString(), planned_end: new Date(s._end).toISOString(), updated_at: Date.now() };
        });
        await axios.post(`${SERVER}/api/buddy/mes/orders`, { customer_id: cid, orders: updated });
        setOrders(updated);
      } catch (e) { console.error('Schedule auto-save failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, orders.length, scheduledOrders.length]);

  // Build machine rows.
  // Phase 7 cleanup 2026-06-25:
  //   - Only include machines that have at least one scheduled order in the visible horizon
  //   - Filter out obvious junk names (test*, blank, sample*)
  // Phase 7 (2026-06-25): hide machines that have NO real planned orders
  const [filterShop, setFilterShop] = useState('');
  const [machineToShop, setMachineToShop] = useState({});  // Phase 12 v18: machine_name -> shop_type
  const [machineToCell, setMachineToCell] = useState({});  // machine_name -> cell (machine group) name
  const [cellOptions, setCellOptions] = useState([]);
  const [filterCell, setFilterCell] = useState('');

  // Fetch machine groups once to build name -> shop_type map
  React.useEffect(() => {
    if (!customerId) return;
    customerbasedshift(customerId, 'machinegroups')
      .then(r => {
        const groups = Array.isArray(r?.[0]?.value) ? r[0].value : [];
        const m = {}; const mc = {}; const cells = [];
        groups.forEach(g => {
          if (g.name) cells.push(g.name);
          (g.machines || []).forEach(mm => {
            const name = typeof mm === 'string' ? mm : mm?.name;
            if (name) { m[name] = g.shop_type; mc[name] = g.name; }
          });
        });
        setMachineToShop(m); setMachineToCell(mc); setCellOptions(cells);
      })
      .catch(() => { setMachineToShop({}); setMachineToCell({}); setCellOptions([]); });
  }, [customerId]);
  const [hideUnplanned, setHideUnplanned] = useState(true);
    const machineRows = useMemo(() => {
    // Junk machine names always hidden
    const junkPattern = /^(test|sample|tmp|temp|demo)/i;
    // For each machine, count orders whose [start, end] overlaps the visible window [t0, t1]
    const inWindowByMachine = new Map();
    const allOrdersByMachine = new Map();
    scheduledOrders.forEach(o => {
      const m = o.assigned_machine || o._machine;
      if (!m || junkPattern.test(m)) return;
      // Phase 12 v18: filter by the MACHINE's actual shop (not the order's), since
      // operations may be pinned to a wrong-shop machine due to legacy data.
      if (filterShop) {
        const mShop = machineToShop[m];
        if (!mShop || mShop.toUpperCase() !== filterShop.toUpperCase()) return;
      }
      if (filterCell) {
        if ((machineToCell[m] || '') !== filterCell) return;
      }
      allOrdersByMachine.set(m, (allOrdersByMachine.get(m) || 0) + 1);
      const startMs = o.planned_start ? (typeof o.planned_start === 'string' ? new Date(o.planned_start).getTime() : new Date(o.planned_start).getTime()) : null;
      const endMs = o.planned_end ? (typeof o.planned_end === 'string' ? new Date(o.planned_end).getTime() : new Date(o.planned_end).getTime()) : null;
      // Overlap test: order intersects window
      // Significant order: duration >= 1 hour AND overlaps the visible window
      // A bar must be at least 4 hours long to count as a real planned order
      const durMs = (startMs && endMs) ? Math.max(0, endMs - startMs) : 0;
      const significant = durMs >= 2 * 60 * 60 * 1000;
      const inWindow = startMs && endMs && endMs > t0 && startMs < t1 && significant;
      if (inWindow) inWindowByMachine.set(m, (inWindowByMachine.get(m) || 0) + 1);
    });
    const machinesToShow = new Set();
    if (hideUnplanned) {
      // Only machines with at least one order whose schedule overlaps the visible window
      for (const m of inWindowByMachine.keys()) machinesToShow.add(m);
    } else {
      for (const m of allOrdersByMachine.keys()) machinesToShow.add(m);
    }
    return Array.from(machinesToShow).sort();
  }, [scheduledOrders, hideUnplanned, t0, t1, filterShop, machineToShop, filterCell, machineToCell]);

  // Phase 7 filters (added 2026-06-25)
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const orderShop = (o) => o?._shop_type || (o?._work_center_code ? String(o._work_center_code).split('-')[0] : '');
  const filteredOrders = useMemo(() => scheduledOrders.filter(o => {
    if (filterShop && orderShop(o).toUpperCase() !== filterShop.toUpperCase()) return false;
    if (filterStatus && (o.status || '').toLowerCase() !== filterStatus.toLowerCase()) return false;
    if (filterPriority && (o.priority || '').toLowerCase() !== filterPriority.toLowerCase()) return false;
    if (filterCustomer && (o.customer_name || '').toLowerCase().indexOf(filterCustomer.toLowerCase()) < 0) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      const blob = ((o.order_id || '') + ' ' + (o.component_number || '') + ' ' + (o.customer_name || '')).toLowerCase();
      if (blob.indexOf(q) < 0) return false;
    }
    return true;
  }), [scheduledOrders, filterShop, filterStatus, filterPriority, filterCustomer, filterSearch]);
  const shopOptions = useMemo(() => Array.from(new Set(scheduledOrders.map(orderShop).filter(Boolean))).sort(), [scheduledOrders]);
  const customerOptions = useMemo(() => Array.from(new Set(scheduledOrders.map(o => o.customer_name).filter(Boolean))).sort(), [scheduledOrders]);

  // Drag-and-drop save: move an order to a new machine and/or start time.
  // Duration is fixed (qty × cycle); we recompute planned_end from cycleSecByCode.
  const moveOrder = async (orderId, newMachine, newStartMs) => {
    const o = orders.find(x => x.order_id === orderId);
    if (!o) return;
    const remaining = Math.max(0, (o.qty_total || 0) - (o.qty_done || 0));
    const cycleSec = cycleSecByCode.get(o.component_number) || 0;
    const durMs = (cycleSec * remaining * 1000) || (60 * 60 * 1000); // fallback 1h
    const newEndMs = newStartMs + durMs;
    const snapshot = orders;
    const updated = orders.map(x => x.order_id === orderId ? {
      ...x,
      assigned_machine: newMachine,
      planned_start: new Date(newStartMs).toISOString(),
      planned_end: new Date(newEndMs).toISOString(),
      updated_at: Date.now(),
    } : x);
    setOrders(updated); // optimistic
    try {
      await axios.post(`${SERVER}/api/buddy/mes/orders`, { customer_id: cid, orders: updated });
    } catch (e) {
      setOrders(snapshot); // rollback
      Swal.fire('Move failed', e?.response?.data?.message || e?.message || 'Unknown', 'error');
    }
  };

  // Toolbar summary: at-risk / rush / idle machines for at-a-glance scan
  const summary = useMemo(() => {
    let atRisk = 0, rush = 0;
    const busyMachines = new Set();
    for (const o of scheduledOrders) {
      if (o.status === 'done' || o.status === 'cancelled') continue;
      const dueMs = o.due_date ? new Date(o.due_date).getTime() : 0;
      // Only flag if planned_end overshoots due by more than 4 hours
      if (dueMs > 0 && o._end > dueMs + 4 * 3600 * 1000) atRisk++;
      if (o.priority === 'rush') rush++;
      const m = o.assigned_machine || o._machine;
      if (m) busyMachines.add(m);
    }
    const idle = Math.max(0, machineRows.length - busyMachines.size);
    return { atRisk, rush, idle };
  }, [scheduledOrders, machineRows]);

  // Auto-plan: write computed _machine / _start / _end into the order, save
  const commitAutoPlan = async () => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Apply auto-planner?',
      html: 'This will set <b>assigned_machine</b>, <b>planned_start</b>, <b>planned_end</b> on every queued order using EDD heuristic.<br>Manually scheduled orders are kept as-is.',
      showCancelButton: true, confirmButtonText: 'Apply',
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const updated = orders.map(o => {
        const s = scheduledOrders.find(x => x.order_id === o.order_id);
        if (!s || !s._auto) return o;
        return { ...o, assigned_machine: s._machine, planned_start: new Date(s._start).toISOString(), planned_end: new Date(s._end).toISOString(), updated_at: Date.now() };
      });
      await axios.post(`${SERVER}/api/buddy/mes/orders`, { customer_id: cid, orders: updated });
      setOrders(updated);
      Swal.fire({ icon: 'success', title: 'Plan applied', timer: 1100, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Save failed', e?.response?.data?.message || e?.message || 'Unknown', 'error');
    } finally { setSaving(false); }
  };

  return (
    <>
            {/* Phase 7 filter bar (2026-06-25) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 16px', background: '#fff',
                      borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Filter:</span>
        <select value={filterShop} onChange={e => setFilterShop(e.target.value)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}>
          <option value="">All Shops</option>
          {shopOptions.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={filterCell} onChange={e => setFilterCell(e.target.value)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}>
          <option value="">All Cells</option>
          {cellOptions.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}>
          <option value="">All Status</option>
          <option value="queue">Queue</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}>
          <option value="">All Priority</option>
          <option value="rush">Rush</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}>
          <option value="">All Customers</option>
          {customerOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
               placeholder="Search WO / part / customer..."
               style={{ flex: 1, minWidth: 180, padding: '4px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#374151',
                          padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer',
                          background: hideUnplanned ? '#fef3c7' : '#fff' }}>
          <input type='checkbox' checked={hideUnplanned} onChange={e => setHideUnplanned(e.target.checked)} style={{ margin: 0 }} />
          Hide unplanned machines
        </label>
        {(filterShop || filterStatus || filterPriority || filterCustomer || filterSearch) && (
          <button onClick={() => { setFilterShop(''); setFilterStatus(''); setFilterPriority(''); setFilterCustomer(''); setFilterSearch(''); }}
                  style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, background: '#fff', color: '#dc2626',
                            border: '1px solid #dc2626', borderRadius: 4, cursor: 'pointer' }}>× Clear</button>
        )}
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>
          {filteredOrders.length} of {scheduledOrders.length} orders shown
        </span>
      </div>

      {/* Schedule toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Holidays</span>
        <select value={includeHolidays ? 'include' : 'exclude'} onChange={e => setIncludeHolidays(e.target.value === 'include')} title="Exclude = skip Sundays + holidays (real calendar). Include = run 24/7 through them." style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}>
          <option value="exclude">Exclude (skip Sun + holidays)</option>
          <option value="include">Include (run 24/7)</option>
        </select>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Horizon</span>
        <select value={horizonDays} onChange={e => setHorizonDays(parseInt(e.target.value, 10))} style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}>
          <option value={2}>2 days</option><option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option>
        </select>
        <button onClick={fetchAll} disabled={loading} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>
          {loading ? '⟳ …' : '⟳ Refresh'}
        </button>
        <button onClick={() => window.location.href = '/work-orders'} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, background: '#fff', color: '#4338ca', border: '1px solid #4338ca', borderRadius: 6, cursor: 'pointer' }}>
          📦 Manage Orders ({orders.length})
        </button>
        <button onClick={commitAutoPlan} disabled={saving || scheduledOrders.filter(o => o._auto).length === 0}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, background: '#F99022', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 4px rgba(249,144,34,0.3)' }}>
          🤖 {saving ? 'Applying…' : `Auto-Plan ${scheduledOrders.filter(o => o._auto).length} order${scheduledOrders.filter(o => o._auto).length !== 1 ? 's' : ''}`}
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
          {/* Summary chip: at-risk / rush / idle machines */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '4px 4px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <SummaryPill icon="⚠" label="At risk" value={summary.atRisk} color={summary.atRisk > 0 ? '#dc2626' : '#9ca3af'} bg={summary.atRisk > 0 ? '#fee2e2' : '#f9fafb'} />
            <SummaryPill icon="🔴" label="Rush" value={summary.rush} color={summary.rush > 0 ? '#b45309' : '#9ca3af'} bg={summary.rush > 0 ? '#fef3c7' : '#f9fafb'} />
            <SummaryPill icon="💤" label="Idle m/c" value={summary.idle} color={summary.idle > 0 ? '#1e3a8a' : '#16a34a'} bg={summary.idle > 0 ? '#dbeafe' : '#dcfce7'} />
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 11, color: '#6b7280' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#9ca3af', marginRight: 4, verticalAlign: 'middle' }} />Auto</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#3b82f6', marginRight: 4, verticalAlign: 'middle' }} />Manual</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#dc2626', marginRight: 4, verticalAlign: 'middle' }} />At risk</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#16a34a', marginRight: 4, verticalAlign: 'middle' }} />Running NOW</span>
          </div>
        </div>
      </div>

      {/* Gantt */}
      {orders.length === 0 && !loading && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: 8, border: '1px dashed #d1d5db' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>No production orders yet</div>
          <div style={{ fontSize: 12, marginBottom: 14 }}>Add orders to see them planned across machines.</div>
          <button onClick={() => window.location.href = '/work-orders'} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 700, background: '#F99022', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+ Add First Order</button>
        </div>
      )}
      {orders.length > 0 && (
        <Gantt t0={t0} t1={t1} machines={machineRows} orders={filteredOrders} floorPlan={floorPlan} components={components} cycleSecByCode={cycleSecByCode} onMoveOrder={moveOrder} />
      )}
    </>
  );
};

// ============================================================
// GANTT CHART
// ============================================================
const Gantt = ({ t0, t1, machines, orders, floorPlan, components = [], cycleSecByCode, onMoveOrder }) => {
  const ROW_H = 40;
  const HEADER_H = 32;
  const LABEL_W = 80;
  const totalMs = t1 - t0;
  const horizonDays = Math.max(1, Math.round(totalMs / 86400000));
  const SVG_W = Math.max(1000, horizonDays * 60);
  const labelStep = horizonDays <= 7 ? 1 : horizonDays <= 14 ? 2 : horizonDays <= 30 ? 3 : 7;
  const chartHeight = HEADER_H + machines.length * ROW_H + 10;

  const days = useMemo(() => {
    const out = [];
    for (let t = t0; t <= t1; t += 86400000) out.push(t);
    return out;
  }, [t0, t1]);

  // Component master lookup (component_number → name)
  const compNameByCode = useMemo(() => {
    const m = new Map();
    for (const c of (components || [])) {
      const code = String(c.component_number || '').trim();
      if (code) m.set(code, c.component_name || c.name || '');
    }
    return m;
  }, [components]);

  const xForTime = (ms) => LABEL_W + ((ms - t0) / totalMs) * (SVG_W - LABEL_W);

  const runningByMachine = useMemo(() => {
    const m = new Map();
    (floorPlan?.machines || []).forEach(x => {
      if (x.status === 'Running' && x.cycle_time_sec && x.parts_made != null && x.parts_target != null) {
        const remaining = Math.max(0, x.parts_target - x.parts_made);
        const start = Date.now() - (x.parts_made * x.cycle_time_sec * 1000);
        const end = Date.now() + (remaining * x.cycle_time_sec * 1000);
        m.set(x.name, { start, end, code: x.active_code, name: x.active_component });
      }
    });
    return m;
  }, [floorPlan]);

  // Which row each machine appears on (for empty-state hint)
  const ordersByMachine = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const k = o.assigned_machine || o._machine;
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(o);
    }
    return m;
  }, [orders]);

  const nowMs = Date.now();

  // Hover tooltip state — captures viewport coords so the floating card follows the cursor.
  const [tip, setTip] = useState(null);
  const handleEnter = (kind, payload, e) => { if (!drag) setTip({ kind, payload, x: e.clientX, y: e.clientY }); };
  const handleMove = (e) => setTip(t => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  const handleLeave = () => setTip(null);

  // Drag-and-drop state. We track the order, where on the bar the user grabbed,
  // and current cursor position in SVG coordinates. On release we snap the
  // ghost to the nearest row + 15-minute slot and persist via onMoveOrder.
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const clientToSvg = (cx, cy) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint(); pt.x = cx; pt.y = cy;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  const SNAP_MIN = 15;
  const SNAP_MS = SNAP_MIN * 60 * 1000;

  const onBarPointerDown = (e, o, barSx, barY, barW) => {
    if (!onMoveOrder) return;
    if (o.status === 'done' || o.status === 'cancelled') return;
    e.stopPropagation();
    const p = clientToSvg(e.clientX, e.clientY);
    setTip(null);
    setDrag({
      order: o, barW,
      cursorSvgX: p.x, cursorSvgY: p.y,
      offsetX: p.x - barSx,
      offsetY: p.y - barY,
    });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const onSvgPointerMove = (e) => {
    if (!drag) return;
    const p = clientToSvg(e.clientX, e.clientY);
    setDrag(d => (d ? { ...d, cursorSvgX: p.x, cursorSvgY: p.y } : d));
  };

  const computeSnap = (d) => {
    const barX = d.cursorSvgX - d.offsetX;
    const barY = d.cursorSvgY - d.offsetY;
    const rowIdx = Math.max(0, Math.min(machines.length - 1, Math.round((barY - HEADER_H) / ROW_H)));
    const newMachine = machines[rowIdx];
    const xRatio = (barX - LABEL_W) / (SVG_W - LABEL_W);
    let newStartMs = t0 + xRatio * totalMs;
    newStartMs = Math.round(newStartMs / SNAP_MS) * SNAP_MS;
    newStartMs = Math.max(Date.now() - 60 * 60 * 1000, newStartMs); // allow up to 1h in past
    return { rowIdx, newMachine, newStartMs };
  };

  const onSvgPointerUp = () => {
    if (!drag) return;
    const d = drag;
    setDrag(null);
    const { newMachine, newStartMs } = computeSnap(d);
    const origMachine = d.order.assigned_machine || d.order._machine;
    const origStart = d.order._start;
    if (newMachine === origMachine && Math.abs(newStartMs - origStart) < SNAP_MS) return; // no real move
    onMoveOrder(d.order.order_id, newMachine, newStartMs);
  };

  const fmtDT = (ms) => ms ? new Date(ms).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '—';
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto', position: 'relative', userSelect: drag ? 'none' : 'auto' }}
         onMouseLeave={handleLeave}>
      <svg ref={svgRef}
           viewBox={`0 0 ${SVG_W} ${chartHeight}`}
           onPointerMove={onSvgPointerMove}
           onPointerUp={onSvgPointerUp}
           onPointerCancel={onSvgPointerUp}
           style={{ width: '100%', height: 'auto', display: 'block', minWidth: SVG_W > 1200 ? SVG_W * 0.7 : undefined, cursor: drag ? 'grabbing' : 'default', touchAction: drag ? 'none' : 'auto' }}>
        {/* Today column shading */}
        {(() => {
          const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
          const tStart = todayStart.getTime();
          const tEnd = tStart + 86400000;
          if (tEnd < t0 || tStart > t1) return null;
          const sx = xForTime(Math.max(tStart, t0));
          const ex = xForTime(Math.min(tEnd, t1));
          return <rect x={sx} y={0} width={Math.max(2, ex - sx)} height={chartHeight} fill="#fef3c7" opacity={0.18} />;
        })()}

        {/* Day header */}
        <rect x={0} y={0} width={SVG_W} height={HEADER_H} fill="#f9fafb" />
        {days.map((d, i) => {
          const showLabel = i % labelStep === 0;
          return (
            <g key={i}>
              <line x1={xForTime(d)} y1={0} x2={xForTime(d)} y2={chartHeight} stroke="#e5e7eb" strokeWidth={0.4} />
              {showLabel && (
                <text x={xForTime(d) + 4} y={20} fontSize={11} fill="#374151" fontWeight={700}>
                  {new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </text>
              )}
            </g>
          );
        })}
        <line x1={LABEL_W} y1={HEADER_H} x2={SVG_W} y2={HEADER_H} stroke="#d1d5db" strokeWidth={0.5} />

        {/* Machine rows */}
        {machines.map((mName, ri) => {
          const y = HEADER_H + ri * ROW_H;
          const rowOrders = ordersByMachine.get(mName) || [];
          const isEmpty = rowOrders.length === 0 && !runningByMachine.get(mName);
          return (
            <g key={mName}>
              {/* Row background */}
              <rect x={0} y={y} width={SVG_W} height={ROW_H} fill={ri % 2 ? '#fafafa' : '#fff'} />
              {/* Machine label */}
              <rect x={0} y={y} width={LABEL_W} height={ROW_H} fill="#f9fafb" stroke="#e5e7eb" strokeWidth={0.4} />
              <text x={6} y={y + ROW_H / 2 + 3} fontSize={9} fontWeight={700} fill="#374151">{mName}</text>

              {/* Empty-row hint */}
              {isEmpty && (
                <text x={LABEL_W + 10} y={y + ROW_H / 2 + 4} fontSize={10} fill="#cbd5e1" fontStyle="italic" fontWeight={600}>
                  · no orders scheduled
                </text>
              )}

              {/* Running NOW bar */}
              {runningByMachine.get(mName) && (() => {
                const r = runningByMachine.get(mName);
                const sx = xForTime(Math.max(r.start, t0));
                const ex = xForTime(Math.min(r.end, t1));
                const w = Math.max(6, ex - sx);
                return (
                  <g style={{ cursor: 'pointer' }}
                     onMouseEnter={(e) => handleEnter('running', { mName, r }, e)}
                     onMouseMove={handleMove}>
                    <rect x={sx} y={y + 5} width={w} height={ROW_H - 10} rx={2} fill="#16a34a" opacity={0.9} />
                    {w > 40 ? (
                      <text x={sx + 6} y={y + ROW_H / 2 + 4} fontSize={10} fill="#fff" fontWeight={800}>▶ NOW · {r.code || ''}</text>
                    ) : (
                      <text x={sx + 2} y={y + ROW_H / 2 + 4} fontSize={9} fill="#fff" fontWeight={800}>▶</text>
                    )}
                  </g>
                );
              })()}

              {/* Order bars for this machine */}
              {rowOrders.map(o => {
                if (drag && drag.order.order_id === o.order_id) return null; // hide while dragging
                const sx = xForTime(Math.max(o._start, t0));
                const ex = xForTime(Math.min(o._end, t1));
                if (ex < LABEL_W || sx > SVG_W) return null;
                const w = Math.max(4, ex - sx);
                const dueMs = o.due_date ? new Date(o.due_date).getTime() : 0;
                const atRisk = dueMs > 0 && o._end > dueMs + 4 * 3600 * 1000;
                const isAuto = o._auto;
                const isDone = o.status === 'done';
                const fill = atRisk ? '#fee2e2' : (isAuto ? '#f3f4f6' : '#dbeafe');
                const stroke = atRisk ? '#dc2626' : (isAuto ? '#9ca3af' : '#3b82f6');
                const textColor = atRisk ? '#7a0000' : (isAuto ? '#374151' : '#1e3a8a');
                const prioColor = PRIORITY_COLOR[o.priority] || PRIORITY_COLOR.normal;
                const draggable = onMoveOrder && o.status !== 'done' && o.status !== 'cancelled';
                // Completion progress — filled overlay + right-aligned %
                const total = Number(o.qty_total) || 0;
                const done  = Number(o.qty_done)  || 0;
                const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                const progFill = isDone ? '#16a34a' : (atRisk ? '#fca5a5' : (isAuto ? '#9ca3af' : '#60a5fa'));
                const progW = Math.max(0, (w - 3) * (pct / 100));
                return (
                  <g key={o.order_id} style={{ cursor: draggable ? 'grab' : 'pointer' }}
                     onPointerDown={draggable ? (e) => onBarPointerDown(e, o, sx, y + 5, w) : undefined}
                     onMouseEnter={(e) => handleEnter('order', o, e)}
                     onMouseMove={handleMove}>
                    <rect x={sx} y={y + 5} width={w} height={ROW_H - 10} rx={3} fill={fill} stroke={stroke} strokeWidth={atRisk ? 1.4 : 0.6} />
                    {/* Priority indicator (left edge tick) */}
                    <rect x={sx} y={y + 5} width={3} height={ROW_H - 10} fill={prioColor} />
                    {/* Completion fill (clipped to the bar via rx so it stays inside rounded corners) */}
                    {progW > 0 && (
                      <rect x={sx + 3} y={y + 7} width={Math.max(0, progW - 1)} height={ROW_H - 14} rx={2} fill={progFill} opacity={isDone ? 0.55 : 0.4} />
                    )}
                    {w > 40 && (() => {
                      // Truncate label based on available bar width so it never overflows.
                      // ~6 px per char at 10pt; reserve 14px for percentage at the end if needed.
                      const reserveForPct = (w > 70 && pct > 0) ? 28 : 8;
                      const availPx = Math.max(0, w - 7 - reserveForPct);
                      const maxChars = Math.max(2, Math.floor(availPx / 5.4));
                      const fullText = (atRisk ? '⚠ ' : '') + cleanOrderId(o.order_id).split(' · ')[0].slice(-6) + ' · ' + cleanComponent(o.component_number);
                      const truncated = fullText.length > maxChars ? fullText.slice(0, Math.max(2, maxChars - 1)) + '…' : fullText;
                      return (
                        <text x={sx + 7} y={y + ROW_H / 2 + 4} fontSize={10} fill={textColor} fontWeight={700}
                              style={{ pointerEvents: 'none' }}>
                          {truncated}
                        </text>
                      );
                    })()}
                    {w > 70 && pct > 0 && (
                      <text x={sx + w - 6} y={y + ROW_H / 2 + 4} fontSize={9} fill={textColor} fontWeight={800} textAnchor="end" opacity={0.9}
                            style={{ pointerEvents: 'none' }}>
                        {pct}%
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* NOW line */}
        {nowMs >= t0 && nowMs <= t1 && (
          <g>
            <line x1={xForTime(nowMs)} y1={HEADER_H} x2={xForTime(nowMs)} y2={chartHeight} stroke="#F99022" strokeWidth={1.2} strokeDasharray="3 2" />
            <rect x={xForTime(nowMs) - 16} y={HEADER_H - 16} width={32} height={14} rx={3} fill="#F99022" />
            <text x={xForTime(nowMs)} y={HEADER_H - 5} fontSize={9} fill="#fff" fontWeight={800} textAnchor="middle">NOW</text>
          </g>
        )}

        {/* Drag ghost overlay — target row highlight + snapped ghost bar + new-start label */}
        {drag && (() => {
          const { rowIdx, newMachine, newStartMs } = computeSnap(drag);
          const ghostY = HEADER_H + rowIdx * ROW_H + 5;
          const ghostSx = xForTime(Math.max(newStartMs, t0));
          const ghostW = Math.max(8, drag.barW);
          const dueMs = drag.order.due_date ? new Date(drag.order.due_date).getTime() : 0;
          const newEndMs = newStartMs + (drag.order._end - drag.order._start);
          const willBeAtRisk = dueMs > 0 && newEndMs > dueMs;
          const stamp = new Date(newStartMs).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
          return (
            <g pointerEvents="none">
              {/* Target row band */}
              <rect x={LABEL_W} y={HEADER_H + rowIdx * ROW_H} width={SVG_W - LABEL_W} height={ROW_H} fill="#fef3c7" opacity={0.55} />
              {/* Ghost bar at snapped position */}
              <rect x={ghostSx} y={ghostY} width={ghostW} height={ROW_H - 10} rx={3}
                    fill={willBeAtRisk ? '#fecaca' : '#bfdbfe'} stroke={willBeAtRisk ? '#dc2626' : '#1e40af'} strokeWidth={1.4} strokeDasharray="4 3" opacity={0.95} />
              {/* Snap label */}
              <g>
                <rect x={ghostSx} y={ghostY - 18} width={Math.max(140, ghostW)} height={15} rx={3} fill="#111827" />
                <text x={ghostSx + 6} y={ghostY - 7} fontSize={10} fill="#fff" fontWeight={700}>
                  📌 {newMachine} · {stamp}
                </text>
              </g>
            </g>
          );
        })()}
      </svg>

      {/* Rich hover card — viewport-positioned, follows the cursor */}
      {tip && tip.kind === 'order' && (() => {
        const o = tip.payload;
        const compName = compNameByCode.get(o.component_number) || '';
        const remaining = Math.max(0, (o.qty_total || 0) - (o.qty_done || 0));
        const cycleSec = cycleSecByCode?.get?.(o.component_number);
        const durHrs = cycleSec && remaining > 0 ? ((cycleSec * remaining) / 3600).toFixed(1) : null;
        const dueMs = o.due_date ? new Date(o.due_date).getTime() : 0;
        const atRisk = dueMs > 0 && o._end > dueMs;
        const slipHrs = atRisk ? Math.round((o._end - dueMs) / 3600000) : 0;
        const prio = String(o.priority || 'normal').toUpperCase();
        const prioColor = PRIORITY_COLOR[o.priority] || PRIORITY_COLOR.normal;
        const right = tip.x > window.innerWidth - 340;
        return (
          <div style={{
            position: 'fixed', left: right ? tip.x - 330 : tip.x + 14, top: Math.max(10, Math.min(tip.y + 14, window.innerHeight - 380)), maxHeight: 'calc(100vh - 20px)', overflowY: 'auto',
            width: 320, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.08)',
            padding: 0, fontSize: 12, color: '#111827', zIndex: 1000, pointerEvents: 'none',
            borderLeft: `4px solid ${prioColor}`,
          }}>
            {/* Header */}
            <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #f1f5f9', background: atRisk ? '#fef2f2' : '#fafafa', borderTopRightRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#111827', letterSpacing: -0.2 }}>
                  {atRisk && <span style={{ marginRight: 4 }}>⚠</span>}{cleanOrderId(o.order_id)}
                </span>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: prioColor, padding: '2px 7px', borderRadius: 999, letterSpacing: 0.5 }}>{prio}</span>
              </div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 700, marginTop: 3 }}>{o.customer_name || '—'}</div>
            </div>
            {/* Body */}
            <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: 5, columnGap: 8 }}>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Component</span>
              <span style={{ fontWeight: 700, color: '#111827' }}>
                <span style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 11 }}>{cleanComponent(o.component_number)}</span>
                {compName && <span style={{ marginLeft: 6, color: '#374151', fontWeight: 500 }}>{compName}</span>}
              </span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Qty</span>
              <span style={{ fontWeight: 700 }}>
                <b style={{ color: '#16a34a' }}>{o.qty_done || 0}</b>
                <span style={{ color: '#9ca3af', fontWeight: 500 }}> / {o.qty_total || 0}</span>
                <span style={{ marginLeft: 6, color: '#6b7280', fontWeight: 600 }}>({remaining} left)</span>
              </span>
              {durHrs && <>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>Est. run</span>
                <span style={{ fontWeight: 700, color: '#111827' }}>{durHrs} h</span>
              </>}
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Machine</span>
              <span style={{ fontWeight: 700, color: o._auto ? '#6b7280' : '#1e3a8a' }}>
                {(o.assigned_machine || o._machine || '—')}
                {o._auto && <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>(auto)</span>}
              </span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Start</span>
              <span style={{ fontWeight: 600 }}>{fmtDT(o._start)}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>End</span>
              <span style={{ fontWeight: 600, color: atRisk ? '#dc2626' : '#111827' }}>{fmtDT(o._end)}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Due</span>
              <span style={{ fontWeight: 700, color: atRisk ? '#dc2626' : '#111827' }}>{fmtDate(o.due_date)}</span>
            </div>
            {atRisk && (
              <div style={{ padding: '6px 12px', background: '#fee2e2', color: '#7a0000', fontSize: 11, fontWeight: 800, borderTop: '1px solid #fecaca', borderBottomLeftRadius: 4, borderBottomRightRadius: 6, letterSpacing: 0.3 }}>
                ⚠ AT RISK — finishes ~{slipHrs}h after due date
              </div>
            )}
            {o.notes && !atRisk && (
              <div style={{ padding: '6px 12px', background: '#f9fafb', color: '#374151', fontSize: 11, fontStyle: 'italic', borderTop: '1px solid #f1f5f9', borderBottomLeftRadius: 4, borderBottomRightRadius: 6 }}>
                {o.notes}
              </div>
            )}
          </div>
        );
      })()}

      {tip && tip.kind === 'running' && (() => {
        const r = tip.payload.r;
        const mName = tip.payload.mName;
        const right = tip.x > window.innerWidth - 280;
        return (
          <div style={{
            position: 'fixed', left: right ? tip.x - 270 : tip.x + 14, top: Math.min(tip.y + 14, window.innerHeight - 130),
            width: 260, background: '#fff', border: '1px solid #16a34a', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', fontSize: 12, color: '#111827', zIndex: 1000, pointerEvents: 'none',
            borderLeft: '4px solid #16a34a',
          }}>
            <div style={{ padding: '8px 12px', background: '#dcfce7', borderBottom: '1px solid #bbf7d0', fontSize: 11, fontWeight: 800, color: '#14532d', letterSpacing: 0.5, borderTopRightRadius: 6 }}>
              ▶ RUNNING NOW · {mName}
            </div>
            <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '70px 1fr', rowGap: 4, columnGap: 8 }}>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Code</span>
              <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 11 }}>{r.code || '—'}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Part</span>
              <span style={{ fontWeight: 700 }}>{r.name || '—'}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>Started</span>
              <span style={{ fontWeight: 600 }}>{fmtDT(r.start)}</span>
              <span style={{ color: '#6b7280', fontWeight: 600 }}>ETA</span>
              <span style={{ fontWeight: 700, color: '#16a34a' }}>{fmtDT(r.end)}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MesPlanning;
